import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { SortableFilterableTableHeader, ColumnFilter, ColumnSort } from '../../components/common/SortableFilterableTableHeader';
import { 
  Search, 
  MoreVertical, 
  ChevronLeft, 
  ChevronRight,
  Home,
  Store,
  ChevronDown,
  Phone,
  MapPin,
  Eye,
  Star,
  Globe,
  RefreshCw
} from 'lucide-react';
import {
  doc,
  getDoc,
  addDoc,
  Timestamp,
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  query as firestoreQuery
} from 'firebase/firestore';
import { getAssignedStores, getAllStores } from '../../utils/storeAssignment';
import type { Store as StoreType } from '../../@types/store';

interface Store {
  id: string;
  customerNo: string;
  name: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  phone: string;
  email: string;
  manager: string;
  type: string;
  status: 'active' | 'inactive';
}

const PageContainer = styled.div<{ isMenuCollapsed: boolean }>`
  display: flex;
  min-height: 100vh;
  background-color: #f7f9ea;
  margin-left: ${props => props.isMenuCollapsed ? '80px' : '280px'};
  transition: margin-left 0.3s ease;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 100px 20px 20px 20px;
  width: 100%;
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  font-size: 14px;
  color: #666;
`;

const BreadcrumbItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BreadcrumbSeparator = styled.span`
  color: #ccc;
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: bold;
  color: #333;
  margin: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const RefreshButton = styled.button<{ spinning?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background-color: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  svg {
    animation: ${props => props.spinning ? 'spin 0.8s linear infinite' : 'none'};
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  &:hover:not(:disabled) {
    background-color: #f9fafb;
    border-color: #9ca3af;
    color: #374151;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 2px solid #e5e7eb;
`;

const Tab = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background-color: ${props => props.active ? '#fff' : 'transparent'};
  border: none;
  border-bottom: 3px solid ${props => props.active ? '#A0C93F' : 'transparent'};
  cursor: pointer;
  font-size: 15px;
  font-weight: ${props => props.active ? '600' : '500'};
  color: ${props => props.active ? '#A0C93F' : '#6b7280'};
  transition: all 0.2s ease;
  position: relative;
  margin-bottom: -2px;

  &:hover {
    color: #A0C93F;
    background-color: #f9fafb;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const TabBadge = styled.span<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background-color: ${props => props.active ? '#A0C93F' : '#e5e7eb'};
  color: ${props => props.active ? '#fff' : '#6b7280'};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 4px;
`;

const SearchFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  background-color: #fff;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
  
  &:focus {
    outline: none;
    border-color: #A0C93F;
    box-shadow: 0 0 0 3px rgba(160, 201, 63, 0.1);
    background-color: #fafafa;
  }
  
  &:hover:not(:focus) {
    border-color: #cbd5e1;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: #fff;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: #f8fafc;
    border-color: #A0C93F;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  &:focus {
    outline: none;
    border-color: #A0C93F;
    box-shadow: 0 0 0 3px rgba(160, 201, 63, 0.1);
  }
`;

const TableContainer = styled.div`
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  overflow: visible;
  border: 1px solid #f1f5f9;
  transition: box-shadow 0.2s ease;
  position: relative;

  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  position: relative;
`;

const TableHeader = styled.thead`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e2e8f0;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  position: relative;
  
  &:first-child {
    padding-left: 24px;
  }
  
  &:last-child {
    padding-right: 24px;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    background-color: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  }

  &:last-child {
    border-bottom: none;
  }

  &:focus-within {
    background-color: #f0f9ff;
    outline: 2px solid #A0C93F;
    outline-offset: -2px;
  }
`;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #374151;
  vertical-align: middle;
  font-weight: 400;
  
  &:first-child {
    padding-left: 24px;
    font-weight: 600;
    color: #1f2937;
  }
  
  &:last-child {
    padding-right: 24px;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  color: #666;
  transition: all 0.2s;

  &:hover {
    background-color: #f5f5f5;
    color: #333;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: #f1f5f9;
    border-color: #A0C93F;
    color: #334155;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  &:focus {
    outline: none;
    border-color: #A0C93F;
    box-shadow: 0 0 0 3px rgba(160, 201, 63, 0.1);
  }
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background-color: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  min-width: 180px;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.95)'};
  transition: all 0.15s ease;
  backdrop-filter: blur(8px);
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  transition: all 0.15s ease;
  position: relative;

  &:hover {
    background-color: #f8fafc;
    color: #1f2937;
    transform: translateX(2px);
  }

  &:active {
    background-color: #f1f5f9;
    transform: translateX(0);
  }

  &:focus {
    outline: none;
    background-color: #f0f9ff;
    color: #1e40af;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  &:only-child {
    border-radius: 8px;
  }
`;

const DropdownIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: #666;
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
`;

const PaginationButton = styled.button<{ active?: boolean }>`
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  background-color: ${props => props.active ? '#A0C93F' : '#fff'};
  color: ${props => props.active ? '#fff' : '#475569'};
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  min-width: 40px;
  position: relative;

  &:hover:not(:disabled) {
    background-color: ${props => props.active ? '#8fb832' : '#f8fafc'};
    border-color: ${props => props.active ? '#8fb832' : '#A0C93F'};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  &:focus {
    outline: none;
    border-color: #A0C93F;
    box-shadow: 0 0 0 3px rgba(160, 201, 63, 0.1);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #6b7280;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f4f6;
  border-top: 3px solid #A0C93F;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #6b7280;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #666;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyStateText = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
`;

const EmptyStateSubtext = styled.p`
  font-size: 14px;
  color: #666;
  text-align: center;
  max-width: 400px;
`;

type TabType = 'assigned' | 'all';

export const StoreVisitScreenDesktop = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState<Store[]>([]);
  const [assignedStores, setAssignedStores] = useState<Store[]>([]);
  const [filteredStores, setFilteredStores] = useState<Store[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('assigned');
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  const [columnSort, setColumnSort] = useState<ColumnSort | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    loadStores();
  }, [user]);

  useEffect(() => {
    filterStores();
  }, [searchQuery, stores, assignedStores, activeTab, columnFilters, columnSort]);

  const loadStores = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const db = getFirestore();
      const storesRef = collection(db, 'Stores');
      
      const querySnapshot = await getDocs(storesRef);
      
      console.log('Total stores found:', querySnapshot.size);
      
      const storesData: Store[] = [];
      const storeTypesData: StoreType[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Skip archived (soft-deleted) stores so they don't appear in Store Visit
        if ((data.status || data.Status) === 'archived') return;
        console.log('Store data:', { id: doc.id, ...data });
        
        const store: Store = {
          id: doc.id,
          customerNo: data.CustomerNo || `CUST${doc.id.slice(-3).toUpperCase()}`,
          name: data.StoreName || 'Unknown Store',
          address: data.Address || '',
          city: data.City || '',
          province: data.Prov || '',
          postalCode: data.PostalCode || '',
          phone: data.Telephone || '',
          email: data.EmailAdddress || '',
          manager: data.SalespersonNo || '',
          type: data.SalesGroup || '',
          status: 'active'
        };
        
        // Store raw data for comprehensive search
        (store as any)._rawData = data;
        
        storesData.push(store);
        
        // Also build StoreType array with assignedToUserIds from Firestore
        storeTypesData.push({
          id: doc.id,
          CustomerNo: store.customerNo,
          StoreName: store.name,
          Address: store.address,
          City: store.city,
          Prov: store.province,
          PostalCode: store.postalCode,
          Telephone: store.phone,
          EmailAdddress: store.email,
          SalespersonNo: store.manager,
          salesGroup: store.type,
          assignedToUserIds: data.assignedToUserIds || [],
          status: 'active',
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      });
      
      console.log('Processed stores:', storesData);

      // Fetch user data to get assigned stores
      const userDoc = await getDoc(doc(db, 'users', user.id));
      const userData = userDoc.data();

      // Get assigned stores using utility function
      // Pass user's assignedStoreIds as fallback
      const assigned = await getAssignedStores(
        user.id, 
        storeTypesData, 
        userData?.salesGroup,
        userData?.assignedStoreIds
      );
      
      // Convert assigned stores back to Store format
      // Create a map of store IDs to raw data for quick lookup
      const storeRawDataMap = new Map<string, any>();
      storesData.forEach(store => {
        storeRawDataMap.set(store.id, (store as any)._rawData);
      });
      
      const assignedData: Store[] = assigned.map(s => {
        const assignedStore: Store = {
          id: s.id,
          customerNo: s.CustomerNo || '',
          name: s.StoreName || 'Unknown Store',
          address: s.Address || '',
          city: s.City || '',
          province: s.Prov || '',
          postalCode: s.PostalCode || '',
          phone: s.Telephone || '',
          email: s.EmailAdddress || '',
          manager: s.SalespersonNo || '',
          type: s.salesGroup || '',
          status: 'active'
        };
        
        // Preserve raw data for comprehensive search
        (assignedStore as any)._rawData = storeRawDataMap.get(s.id) || {};
        
        return assignedStore;
      });

      console.log(`Assigned stores for user ${user.id}:`, assignedData.length);
      
      setStores(storesData);
      setAssignedStores(assignedData);
      setFilteredStores(activeTab === 'assigned' ? assignedData : storesData);
    } catch (error) {
      console.error('Error loading stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadStores();
    } finally {
      setIsRefreshing(false);
    }
  };

  const filterStores = () => {
    // Determine which store list to filter based on active tab
    const sourceStores = activeTab === 'assigned' ? assignedStores : stores;

    // Apply text search filter
    // Check both mapped fields and raw database fields to catch all variations
    let filtered = sourceStores;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(store => {
        const rawData = (store as any)._rawData || {};
        
        // Helper to safely check if a value contains the query
        const matches = (value: any): boolean => {
          if (!value) return false;
          return String(value).toLowerCase().includes(query);
        };
        
        return (
          matches(store.customerNo) ||
          matches((store as any).CustomerNo) ||
          matches(rawData.CustomerNo) ||
          matches(rawData.customerNo) ||
          matches(store.name) ||
          matches((store as any).StoreName) ||
          matches((store as any).Name) ||
          matches(rawData.StoreName) ||
          matches(rawData.Name) ||
          matches(rawData.name) ||
          matches(store.city) ||
          matches((store as any).City) ||
          matches(rawData.City) ||
          matches(rawData.city) ||
          matches(store.address) ||
          matches((store as any).Address) ||
          matches(rawData.Address) ||
          matches(rawData.address) ||
          matches(store.email) ||
          matches((store as any).Email) ||
          matches(rawData.EmailAdddress) ||
          matches(rawData.EmailAddress) ||
          matches(rawData.Email) ||
          matches(rawData.email) ||
          matches(store.phone) ||
          matches(store.telephone) ||
          matches((store as any).Telephone) ||
          matches((store as any).Phone) ||
          matches(rawData.Telephone) ||
          matches(rawData.Phone) ||
          matches(rawData.phone) ||
          matches(store.salesGroup) ||
          matches((store as any).SalesGroup) ||
          matches(rawData.SalesGroup) ||
          matches(rawData.salesGroup) ||
          matches(store.salespersonNo) ||
          matches((store as any).SalespersonNo) ||
          matches(rawData.SalespersonNo) ||
          matches(rawData.salespersonNo) ||
          matches(store.salesPersonName) ||
          matches((store as any).SalesPersonName) ||
          matches(rawData.SalesPersonName) ||
          matches(rawData.salesPersonName) ||
          matches(store.sortField) ||
          matches((store as any).SortField) ||
          matches(rawData.SortField) ||
          matches(rawData.sortField) ||
          matches(store.state) ||
          matches(store.province) ||
          matches((store as any).Prov) ||
          matches((store as any).Province) ||
          matches(rawData.Prov) ||
          matches(rawData.Province) ||
          matches(rawData.province) ||
          matches(rawData.state) ||
          matches(store.pc) ||
          matches(store.postalCode) ||
          matches((store as any).PostalCode) ||
          matches((store as any).PC) ||
          matches(rawData.PostalCode) ||
          matches(rawData.PC) ||
          matches(rawData.pc) ||
          matches(store.storeNumber) ||
          matches((store as any).StoreNumber) ||
          matches(rawData.StoreNumber) ||
          matches(rawData.storeNumber)
        );
      });
    }

    // Apply column filters
    columnFilters.forEach(filter => {
      if (filter.value) {
        filtered = filtered.filter(store => {
          const storeValue = String(store[filter.column as keyof Store] || '').toLowerCase();
          return storeValue === filter.value?.toLowerCase();
        });
      }
    });

    // Apply sorting
    if (columnSort && columnSort.direction) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = String(a[columnSort.column as keyof Store] || '').toLowerCase();
        const bValue = String(b[columnSort.column as keyof Store] || '').toLowerCase();
        
        if (columnSort.direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      });
    }

    setFilteredStores(filtered);
    setCurrentPage(1);
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchQuery(''); // Clear search when switching tabs
    setCurrentPage(1); // Reset to first page
    setColumnFilters([]); // Clear filters when switching tabs
    setColumnSort(null); // Clear sort when switching tabs
  };

  const handleStoreAction = (store: Store, action: string) => {
    console.log(`${action} clicked for store:`, store);
    setOpenDropdown(null); // Close dropdown after action
    
    switch (action) {
      case 'details':
        navigate(`/store-details/${store.id}`);
        break;
      case 'call':
        if (store.phone) {
          // Open phone dialer in new tab/window to keep user on current screen
          window.open(`tel:${store.phone}`, '_blank');
        }
        break;
      case 'visit':
        // Navigate to store details with store-visit tab explicitly set
        navigate(`/store-details/${store.id}?tab=store-visit`);
        break;
      default:
        break;
    }
  };

  const handleRowClick = (store: Store) => {
    // Navigate to store details with store-visit tab explicitly set
    navigate(`/store-details/${store.id}?tab=store-visit`);
  };

  const handleDropdownToggle = (storeId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    setOpenDropdown(openDropdown === storeId ? null : storeId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdown(null);
    };

    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  // Pagination logic
  const totalPages = Math.ceil(filteredStores.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentStores = filteredStores.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <PaginationButton
            key={i}
            active={i === currentPage}
            onClick={() => handlePageChange(i)}
          >
            {i.toString().padStart(2, '0')}
          </PaginationButton>
        );
      }
    } else {
      // Show first page
      pages.push(
        <PaginationButton
          key={1}
          active={1 === currentPage}
          onClick={() => handlePageChange(1)}
        >
          01
        </PaginationButton>
      );

      // Show current page and surrounding pages
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      if (startPage > 2) {
        pages.push(<span key="ellipsis1">...</span>);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <PaginationButton
            key={i}
            active={i === currentPage}
            onClick={() => handlePageChange(i)}
          >
            {i.toString().padStart(2, '0')}
          </PaginationButton>
        );
      }

      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2">...</span>);
      }

      // Show last page
      if (totalPages > 1) {
        pages.push(
          <PaginationButton
            key={totalPages}
            active={totalPages === currentPage}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages.toString().padStart(2, '0')}
          </PaginationButton>
        );
      }
    }

    return pages;
  };

  const renderLayout = (children: React.ReactNode) => (
    <PageContainer isMenuCollapsed={false}>
      {children}
    </PageContainer>
  );

  if (loading) {
    return renderLayout(
      <MainContent>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>{t('storeVisit.loadingStores')}</LoadingText>
        </LoadingContainer>
      </MainContent>
    );
  }

  return renderLayout(
    <MainContent>
      <BreadcrumbContainer>
        <BreadcrumbItem>
          <Home size={16} />
          {t('storeVisit.home')}
        </BreadcrumbItem>
        <BreadcrumbSeparator>/</BreadcrumbSeparator>
        <BreadcrumbItem>
          <Store size={16} />
          {t('storeVisit.title')}
        </BreadcrumbItem>
      </BreadcrumbContainer>

      <TitleRow>
        <PageTitle>{t('storeVisit.title')}</PageTitle>
        <RefreshButton
          onClick={handleRefresh}
          disabled={isRefreshing || loading}
          spinning={isRefreshing}
        >
          <RefreshCw size={16} />
          {isRefreshing ? t('storeVisit.refreshing', 'Refreshing...') : t('storeVisit.refresh', 'Refresh')}
        </RefreshButton>
      </TitleRow>

      <TabContainer>
        <Tab 
          active={activeTab === 'assigned'} 
          onClick={() => handleTabChange('assigned')}
        >
          <Star size={18} />
          {t('storeVisit.myStores')}
          <TabBadge active={activeTab === 'assigned'}>
            {assignedStores.length}
          </TabBadge>
        </Tab>
        <Tab 
          active={activeTab === 'all'} 
          onClick={() => handleTabChange('all')}
        >
          <Globe size={18} />
          {t('storeVisit.allStores')}
          <TabBadge active={activeTab === 'all'}>
            {stores.length}
          </TabBadge>
        </Tab>
      </TabContainer>

          <SearchFilterContainer>
            <SearchContainer>
              <SearchIcon>
                <Search size={20} />
              </SearchIcon>
              <SearchInput
                type="text"
                placeholder={t('storeVisit.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchContainer>
          </SearchFilterContainer>

          {filteredStores.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>
                <Store size={48} />
              </EmptyStateIcon>
              <EmptyStateText>{t('storeVisit.noStoresFound')}</EmptyStateText>
              <EmptyStateSubtext>
                {searchQuery
                  ? t('storeVisit.noStoresFoundHint', 'Verify your search details are correct. If the store still doesn\'t appear, click Refresh to load the latest data.')
                  : t('storeVisit.noStoresAvailable')}
              </EmptyStateSubtext>
            </EmptyState>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <SortableFilterableTableHeader
                    columns={[
                      { key: 'customerNo', label: t('storeVisit.customerNo'), filterable: true, sortable: true },
                      { key: 'name', label: t('storeVisit.storeName'), filterable: true, sortable: true },
                      { key: 'address', label: t('storeVisit.address'), filterable: true, sortable: true },
                      { key: 'city', label: t('storeVisit.city'), filterable: true, sortable: true },
                      { key: 'province', label: t('storeVisit.province'), filterable: true, sortable: true },
                      { key: 'postalCode', label: t('storeVisit.postalCode'), filterable: true, sortable: true },
                      { key: 'email', label: t('storeVisit.contactEmail'), filterable: true, sortable: true },
                      { key: 'phone', label: t('storeVisit.phoneNumber'), filterable: true, sortable: true },
                      { key: 'actions', label: t('storeVisit.actions'), filterable: false, sortable: false }
                    ]}
                    data={activeTab === 'assigned' ? assignedStores : stores}
                    onFilterChange={setColumnFilters}
                    onSortChange={setColumnSort}
                    currentFilters={columnFilters}
                    currentSort={columnSort}
                  />
                  <TableBody>
                    {currentStores.map((store) => (
                      <TableRow key={store.id} onClick={() => handleRowClick(store)}>
                        <TableCell>{store.customerNo}</TableCell>
                        <TableCell>{store.name}</TableCell>
                        <TableCell>{store.address}</TableCell>
                        <TableCell>{store.city}</TableCell>
                        <TableCell>{store.province}</TableCell>
                        <TableCell>{store.postalCode}</TableCell>
                        <TableCell>{store.email}</TableCell>
                        <TableCell>{store.phone}</TableCell>
                        <TableCell>
                          <DropdownContainer>
                            <DropdownButton
                              onClick={(e) => handleDropdownToggle(store.id, e)}
                              title={t('storeVisit.actions')}
                            >
                              {t('storeVisit.actions')}
                              <ChevronDown size={14} />
                            </DropdownButton>
                            <DropdownMenu isOpen={openDropdown === store.id}>
                              <DropdownItem onClick={() => handleStoreAction(store, 'details')}>
                                <DropdownIcon>
                                  <Eye size={16} />
                                </DropdownIcon>
                                {t('storeVisit.details')}
                              </DropdownItem>
                              <DropdownItem onClick={() => handleStoreAction(store, 'call')}>
                                <DropdownIcon>
                                  <Phone size={16} />
                                </DropdownIcon>
                                {t('storeVisit.call')}
                              </DropdownItem>
                              <DropdownItem onClick={() => handleStoreAction(store, 'visit')}>
                                <DropdownIcon>
                                  <MapPin size={16} />
                                </DropdownIcon>
                                {t('storeVisit.visit')}
                              </DropdownItem>
                            </DropdownMenu>
                          </DropdownContainer>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <PaginationContainer>
                  <PaginationButton
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={16} />
                  </PaginationButton>
                  {renderPagination()}
                  <PaginationButton
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight size={16} />
                  </PaginationButton>
                </PaginationContainer>
              )}
            </>
          )}
        </MainContent>
  );
};
